import React, { useEffect, useMemo, useState } from 'react';
import styles from './DirectorsBusinessLoans.module.scss';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { ShimmerThumbnail } from 'react-shimmer-effects';
import clsx from 'clsx';
import { fetchBusinessLoansAndRelatedParties, fetchEnquiriesAndRelatedParties } from '../../../../store/dashboard/creditBureau/actions';
import CategorySelector from '../../../../common/CategorySelector/CategorySelector';
import { ctaPurpleRight } from '../../../../assets/hostedassets';
import { useAppSelector } from '../../../../app/hooks';

type Header = {
    name: string;
    key: string;
};

type TableData = {
    headers: Header[];
    data: Record<string, any>[];
};

// Define the preferred order of headers for the Directors' Business Loans table
const LOANS_HEADER_ORDER = [
    'sanctionDate',
    'name',
    'directorIn',
    'investorName',
    'sanctionedLimit',
    'loanType',
    'accountStatus',
    'delays',
    'currentOverdue',
    'emi',
    'tenure',
    'outstandingAmount',
    'accountRemarks',
    'frequency',
    'ownership',
    'lastReported',
];

// Function to reorder headers based on preferred order
const reorderHeaders = (headers: Header[], orderConfig: string[]): Header[] => {
    const orderedHeaders: Header[] = [];
    const remainingHeaders: Header[] = [];

    // First, add headers in the specified order
    orderConfig.forEach((key) => {
        const foundHeader = headers.find((h) => h.key === key);
        if (foundHeader) {
            orderedHeaders.push(foundHeader);
        }
    });

    // Then, add any remaining headers that weren't in the order config
    headers.forEach((header) => {
        if (!orderConfig.includes(header.key)) {
            remainingHeaders.push(header);
        }
    });

    return [...orderedHeaders, ...remainingHeaders];
};

function DirectorsBusinessLoans() {
    const dispatch = useDispatch();
    const params = useParams();

    const [loansLoading, setLoansLoading] = useState(false);
    const [enquiriesLoading, setEnquiriesLoading] = useState(false);
    const [loansTableData, setLoansTableData] = useState<TableData | null>(null);
    const [enquiriesTableData, setEnquiriesTableData] = useState<TableData | null>(null);
    const [selectedLoansBureau, setSelectedLoansBureau] = useState('');
    const [selectedEnquiriesBureau, setSelectedEnquiriesBureau] = useState('');
    // Simple cache to avoid re-fetching the same data
    const [loansCache, setLoansCache] = useState<Record<string, TableData | null>>({});
    const [enquiriesCache, setEnquiriesCache] = useState<Record<string, TableData | null>>({});

    // Get creditBureauConfig from Redux state
    const creditBureauConfig = useAppSelector((state) => state.investor?.config?.tabConfigs?.creditBureau);

    // Memoize BUREAU_TYPES to prevent recreation on every render
    const BUREAU_TYPES = useMemo(
        () =>
            Object.values(creditBureauConfig?.tabsToDisplay || {})
                .filter((item: any) => item.isActive && item.name !== 'All')
                .map((item: any) => ({
                    id: item?.name,
                    label: item?.displayName,
                    isDefault: item.isDefault,
                    logoUrl: item.logoUrl,
                })) || [],
        [creditBureauConfig?.tabsToDisplay],
    );

    // Get default bureau
    const defaultBureau = useMemo(() => BUREAU_TYPES.find((item: any) => item.isDefault) || BUREAU_TYPES[0], [BUREAU_TYPES]);

    // Initialize selected bureaus with default on mount
    useEffect(() => {
        if (defaultBureau?.id && !selectedLoansBureau) {
            setSelectedLoansBureau(defaultBureau.id);
        }
        if (defaultBureau?.id && !selectedEnquiriesBureau) {
            setSelectedEnquiriesBureau(defaultBureau.id);
        }
    }, [defaultBureau?.id]);

    // Fetch loans data when selected bureau changes
    useEffect(() => {
        if (!selectedLoansBureau || !params.id) return;

        // Check cache first
        if (loansCache[selectedLoansBureau] !== undefined) {
            setLoansTableData(loansCache[selectedLoansBureau]);
            return;
        }

        // Fetch data
        setLoansLoading(true);
        dispatch(
            fetchBusinessLoansAndRelatedParties(
                {
                    orgId: params.id as string,
                    bureauName: selectedLoansBureau,
                },
                {
                    onSuccess: (response: any) => {
                        const tableData: TableData | null =
                            response?.cibilData && response.cibilData.length > 0
                                ? { headers: response.headers || [], data: response.cibilData }
                                : null;

                        setLoansTableData(tableData);
                        setLoansCache((prev) => ({ ...prev, [selectedLoansBureau]: tableData }));
                        setLoansLoading(false);
                    },
                    onError: () => {
                        setLoansTableData(null);
                        setLoansCache((prev) => ({ ...prev, [selectedLoansBureau]: null }));
                        setLoansLoading(false);
                    },
                },
            ),
        );
    }, [selectedLoansBureau, params.id, dispatch]);

    // Fetch enquiries data when selected bureau changes
    useEffect(() => {
        if (!selectedEnquiriesBureau || !params.id) return;

        // Check cache first
        if (enquiriesCache[selectedEnquiriesBureau] !== undefined) {
            setEnquiriesTableData(enquiriesCache[selectedEnquiriesBureau]);
            return;
        }

        // Fetch data
        setEnquiriesLoading(true);
        dispatch(
            fetchEnquiriesAndRelatedParties(
                {
                    orgId: params.id as string,
                    bureauName: selectedEnquiriesBureau,
                },
                {
                    onSuccess: (response: any) => {
                        const tableData: TableData | null =
                            response?.cibilData && response.cibilData.length > 0
                                ? { headers: response.headers || [], data: response.cibilData }
                                : null;

                        setEnquiriesTableData(tableData);
                        setEnquiriesCache((prev) => ({ ...prev, [selectedEnquiriesBureau]: tableData }));
                        setEnquiriesLoading(false);
                    },
                    onError: () => {
                        setEnquiriesTableData(null);
                        setEnquiriesCache((prev) => ({ ...prev, [selectedEnquiriesBureau]: null }));
                        setEnquiriesLoading(false);
                    },
                },
            ),
        );
    }, [selectedEnquiriesBureau, params.id, dispatch]);

    const handleViewAllLoans = () => {
        const newPath = `/investor/app/portfolio/${params.id}?page=1&type=deals&tab=aicaReport&partnerOrgId=${params.id}&selectedTab=creditBureau&bureauType=${selectedLoansBureau}&report=creditSummaryCombinedReport&viewMode=table`;
        window.open(newPath, '_blank');
    };

    const formatCellValue = (value: any, key: string, row: Record<string, any>) => {
        if (value === null || value === undefined || value === '') return '-';

        // Check if it's an amount field and format accordingly
        const amountKeys = [
            'sanctionedAmount',
            'currentBalance',
            'outstandingAmount',
            'emi',
            'enquiryAmountValue',
            'sanctionedLimit',
            'currentOverdue',
        ];
        if (amountKeys.includes(key)) {
            const numericValue = typeof value === 'string' ? parseFloat(value.replace(/[₹,\s]/g, '')) : value;
            if (!isNaN(numericValue)) {
                return `₹${numericValue.toLocaleString('en-IN')}`;
            }
        }

        // For directorIn column, add asterisk if value > 1
        if (key === 'directorIn') {
            const numericValue = typeof value === 'number' ? value : parseInt(value, 10);
            if (!isNaN(numericValue) && numericValue > 1) {
                return `${value}*`;
            }
        }

        return value;
    };

    // Check if any row has directorIn > 1 to determine if footnote should be shown
    const hasMultipleDirectorships = (tableData: TableData | null): boolean => {
        if (!tableData?.data?.length) return false;
        return tableData.data.some((row) => {
            const directorInValue = row.directorIn;
            const numericValue = typeof directorInValue === 'number' ? directorInValue : parseInt(directorInValue, 10);
            return !isNaN(numericValue) && numericValue > 1;
        });
    };

    const renderLoansTable = () => {
        if (loansLoading) {
            return (
                <div className={styles.ShimmerContainer}>
                    <ShimmerThumbnail height={200} rounded className={styles.ShimmerThumbnail} />
                </div>
            );
        }

        if (!loansTableData || !loansTableData.data || loansTableData.data.length === 0) {
            return (
                <div className={styles.EmptyState}>
                    <span>No data available for this Bureau</span>
                </div>
            );
        }

        const showFootnote = hasMultipleDirectorships(loansTableData);
        // Reorder headers for loans table based on specified order
        const orderedHeaders = reorderHeaders(loansTableData.headers, LOANS_HEADER_ORDER);

        return (
            <>
                <div className={styles.TableContainer}>
                    <table className={styles.Table}>
                        <thead className={styles.TableHead}>
                            <tr className={styles.TableHeadRow}>
                                {orderedHeaders.map((header, index) => (
                                    <th
                                        key={index}
                                        className={clsx(styles.HeadRowItem, {
                                            [styles.FirstHeadRowItem]: index === 0,
                                        })}
                                    >
                                        {header.name}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className={styles.TableBody}>
                            {loansTableData.data.map((row, i) => (
                                <tr className={styles.TableBodyRow} key={i}>
                                    {orderedHeaders.map((header, j) => (
                                        <td
                                            className={clsx(styles.RowItem, {
                                                [styles.FirstColumnItem]: j === 0,
                                            })}
                                            key={j}
                                        >
                                            {formatCellValue(row[header.key], header.key, row)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {showFootnote && (
                    <div className={styles.NoteText}>
                        <span className={styles.Bold}>*Note:</span> Bureau data may include borrowings in other companies where they are
                        directors.
                    </div>
                )}
            </>
        );
    };

    const renderEnquiriesTable = () => {
        if (enquiriesLoading) {
            return (
                <div className={styles.ShimmerContainer}>
                    <ShimmerThumbnail height={200} rounded className={styles.ShimmerThumbnail} />
                </div>
            );
        }

        if (!enquiriesTableData || !enquiriesTableData.data || enquiriesTableData.data.length === 0) {
            return (
                <div className={styles.EmptyState}>
                    <span>No data available for this Bureau</span>
                </div>
            );
        }

        const showFootnote = hasMultipleDirectorships(enquiriesTableData);

        return (
            <>
                <div className={styles.TableContainer}>
                    <table className={styles.Table}>
                        <thead className={styles.TableHead}>
                            <tr className={styles.TableHeadRow}>
                                {enquiriesTableData.headers.map((header, index) => (
                                    <th
                                        key={index}
                                        className={clsx(styles.HeadRowItem, {
                                            [styles.FirstHeadRowItem]: index === 0,
                                        })}
                                    >
                                        {header.name}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className={styles.TableBody}>
                            {enquiriesTableData.data.map((row, i) => (
                                <tr className={styles.TableBodyRow} key={i}>
                                    {enquiriesTableData.headers.map((header, j) => (
                                        <td
                                            className={clsx(styles.RowItem, {
                                                [styles.FirstColumnItem]: j === 0,
                                            })}
                                            key={j}
                                        >
                                            {formatCellValue(row[header.key], header.key, row)}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {showFootnote && (
                    <div className={styles.NoteText}>
                        <span className={styles.Bold}>*Note:</span> Bureau data may include borrowings in other companies where they are
                        directors.
                    </div>
                )}
            </>
        );
    };

    // Hide entire component if no bureau types available
    if (!BUREAU_TYPES.length) {
        return null;
    }

    return (
        <>
            {/* Directors' Business Loans Table */}
            <div className={styles.DirectorsBusinessLoans}>
                <div className={styles.Header}>
                    <div className={styles.HeaderLeft}>
                        <h2 className={styles.Heading}>Directors' Business Loans</h2>
                        {BUREAU_TYPES.length > 1 && (
                            <div className={styles.CategorySelector}>
                                <CategorySelector
                                    categoryOptions={BUREAU_TYPES}
                                    selectedCategory={selectedLoansBureau}
                                    changeSelectedTab={setSelectedLoansBureau}
                                />
                            </div>
                        )}
                    </div>
                    <div className={styles.HeaderRight}>
                        {!loansLoading && loansTableData?.data && loansTableData.data.length > 0 && (
                            <div className={styles.ViewAllCta} onClick={handleViewAllLoans}>
                                <img src={ctaPurpleRight} alt="" />
                                View All Loans
                            </div>
                        )}
                        <div className={styles.DataSource}>
                            Data Source: <span className={styles.DataSourceText}>Credit Bureau</span>
                        </div>
                    </div>
                </div>
                <div className={styles.Content}>{renderLoansTable()}</div>
            </div>

            {/* Directors' Business Loans Enquiries Table */}
            <div className={styles.DirectorsBusinessLoans}>
                <div className={styles.Header}>
                    <div className={styles.HeaderLeft}>
                        <h2 className={styles.Heading}>Directors' Business Loans Enquiries</h2>
                        {BUREAU_TYPES.length > 1 && (
                            <div className={styles.CategorySelector}>
                                <CategorySelector
                                    categoryOptions={BUREAU_TYPES}
                                    selectedCategory={selectedEnquiriesBureau}
                                    changeSelectedTab={setSelectedEnquiriesBureau}
                                />
                            </div>
                        )}
                    </div>
                    <div className={styles.HeaderRight}>
                        <div className={styles.DataSource}>
                            Data Source: <span className={styles.DataSourceText}>Credit Bureau</span>
                        </div>
                    </div>
                </div>
                <div className={styles.Content}>{renderEnquiriesTable()}</div>
            </div>
        </>
    );
}

export default DirectorsBusinessLoans;
